import React, { useState, useEffect } from "react"
import { ExtractId } from "../../../util"
import Paginator from "../../Paginator"
import { getManageEstateList } from "../../../api/manage_estate_list"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../LoadingContext"
import { useConnectionError } from "../../ConnectionErrorContext"

import ManageEstateCreate from "./create"
import ManageEstateUpdate from "./update"
import ManageEstateDelete from "./delete"

import DashboardLayout from "../../components/DashboardLayout"
import DashboardNavbar from "../../components/DashboardNavbar"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Button from "@mui/material/Button"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"

export interface ManageEstateData {
  id: string
  catv_id: string
  type: string
  name: string
  address: string
  phone: string
  create_date: number
  update_date: number
  is_delete: number
  facility_count: number
}

const ManageEstateTileList: React.FC = () => {
  const { startLoading, stopLoading } = useLoading()
  const [errorMessage, setErrorMessage] = useState("")
  const { showConnectionError } = useConnectionError()
  const navigate = useNavigate()

  const [searchText, setSearchText] = useState("")
  const [estateData, setEstateData] = useState<ManageEstateData[]>([])
  const [filteredData, setFilteredData] = useState<ManageEstateData[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25

  const [estateCreate, setEstateCreate] = useState<boolean>(false)
  const [estateEdit, setEstateEdit] = useState<boolean>(false) // 編集モーダルの表示/非表示
  const [estateDelete, setEstateDelete] = useState<boolean>(false) // 編集モーダルの表示/非表示
  const [selectedEstate, setSelectedEstate] = useState<ManageEstateData | null>(null) // 編集対象のサービス提供元データ

  // 開発者アカウントでの閲覧を可能とする
  const [isViewMode, setIsViewMode] = useState<boolean>(false)

  if (localStorage.getItem("session_code") == "") {
    navigate("/login")
    return <></>
  }

  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const fetchManageEstateList = async () => {
    try {
      startLoading()
      const manageEstateData = await getManageEstateList()

      const filteredEstateData = manageEstateData.estate_list
      setEstateData(filteredEstateData)

      // 開発者アカウントでの閲覧を可能とする
      const auth = localStorage.getItem("account_type")
      if (auth == "service") {
        let isViewModeWork = true
        const view_mode = localStorage.getItem("view_mode")
        if (view_mode == "2") {
          const estate_id = localStorage.getItem("show_mode_estate")
          if (estate_id === "" || estate_id === "-") {
            isViewModeWork = false
          }
        }
        setIsViewMode(isViewModeWork)
      }
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else if (!error.response) {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }

  useEffect(() => {
    fetchManageEstateList()
  }, [])

  // 検索処理
  const handleSearch = () => {
    const filtered = estateData.filter((estate) => {
      const matchesText =
        searchText === "" ||
        estate.name?.includes(searchText) ||
        estate.address?.includes(searchText) ||
        estate.phone?.includes(searchText)

      return matchesText
    })
    setFilteredData(filtered)
    setCurrentPage(1)
  }

  // 初期表示時に初期ページを表示
  useEffect(() => {
    setFilteredData(estateData)
  }, [estateData])

  const handleCreate = () => {
    //console.log("追加ボタンがクリックされた ");
    setEstateCreate(true)
  }

  const handleEdit = (id: string) => {
    //console.log("編集ボタンがクリックされた " + id);
    const estateToEdit = estateData.find((estate) => estate.id === id)
    if (estateToEdit) {
      setSelectedEstate(estateToEdit) // 編集対象をセット
      setEstateEdit(true) // 編集モーダルを表示
    }
  }
  const handleDelete = (id: string) => {
    //console.log("削除ボタンがクリックされた " + id);
    const estateToEdit = estateData.find((estate) => estate.id === id)
    if (estateToEdit) {
      setSelectedEstate(estateToEdit) // 編集対象をセット
      setEstateDelete(true) // 編集モーダルを表示
    }
  }

  const handleClose = async (num: number) => {
    if (num === 1) {
      // APIを再取得
      await fetchManageEstateList()

      // データ更新後に検索を行う
      setEstateCreate(false)
      setEstateEdit(false)
      setEstateDelete(false)

      // フィルタリングを再実行して、画面を更新
      handleSearch()
    } else {
      setEstateCreate(false)
      setEstateEdit(false)
      setEstateDelete(false)
    }
  }

  const totalPages = Math.ceil(filteredData.length / itemsPerPage)

  const validate = (message: string) => {
    setSearchText(message)

    if (message.length > 10) {
      setErrorMessage("入力文字数は10文字以内です")
    } else {
      setErrorMessage("") // エラーがない場合はクリア
    }
  }

  return (
    <DashboardLayout>
      {estateCreate && <ManageEstateCreate onClose={handleClose} />}
      {estateEdit && selectedEstate && (
        <ManageEstateUpdate
          onClose={handleClose}
          estateData={selectedEstate} // 編集するサービス提供元データを渡す
        />
      )}
      {estateDelete && selectedEstate && (
        <ManageEstateDelete
          onClose={handleClose}
          estateData={selectedEstate} // 削除するサービス提供元データを渡す
        />
      )}
      <MDBox mb={4}>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3} pb={3}>
        <MDBox>
          <MDTypography variant="h4" gutterBottom>
            不動産業者一覧
          </MDTypography>
        </MDBox>
      </MDBox>
      {/* 検索フォーム */}
      <MDBox
        mb={2}
        display="flex"
        gap={3}
        flexWrap="wrap"
        alignItems="center"
        sx={{ fontSize: "0.875rem", color: "text.primary" }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <div style={{ position: "relative", display: "inline-block" }}>
            <MDInput
              label="不動産業者名や住所で検索"
              value={searchText}
              onChange={(e) => validate(e.target.value)}
              sx={{ width: "240px", backgroundColor: "#FFFFFF", borderRadius: "10px" }}
              error={Boolean(errorMessage)} // エラー時のスタイル変更
            />
            {errorMessage && (
              <div
                style={{
                  position: "absolute",
                  top: "100%", // 入力欄の下に配置
                  left: "0",
                  fontSize: "12px",
                  color: "red",
                  marginTop: "4px",
                  whiteSpace: "nowrap"
                }}
              >
                {errorMessage}
              </div>
            )}
          </div>
        </MDBox>
        <MDButton variant="gradient" color="info" size="medium" onClick={handleSearch} disabled={errorMessage !== ""}>
          <Icon>search</Icon>&nbsp; 検索
        </MDButton>
        <MDBox sx={{ width: "calc(100% - 482px)" }} />
        {!isViewMode && (
          <MDBox>
            <MDButton variant="gradient" color="info" size="medium" onClick={handleCreate}>
              追加
            </MDButton>
          </MDBox>
        )}
      </MDBox>
      {/* 不動産業者一覧 */}
      <MDBox mt={4}>
        <TableContainer sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 300px)" }}>
          <Table stickyHeader>
            <TableHead sx={{ position: "sticky", display: "table-header-group", zIndex: 3 }}>
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    fontSize: "0.875rem",
                    color: "text.primary",
                    fontWeight: "bold",
                    backgroundColor: "#FFFFFF"
                  }
                }}
              >
                <TableCell align="left">不動産業者ID</TableCell>
                <TableCell align="left">不動産業者名</TableCell>
                <TableCell align="left">住所</TableCell>
                <TableCell align="left">電話番号</TableCell>
                {!isViewMode && (
                  <>
                    <TableCell align="center" style={{ minWidth: "75px" }}>
                      編集
                    </TableCell>
                    <TableCell align="center" style={{ minWidth: "75px" }}>
                      削除
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "0.875rem",
                      color: "text.primary",
                      fontWeight: "normal",
                      zIndex: 0
                    }
                  }}
                >
                  <TableCell align="left">
                    <div
                      style={{
                        minWidth: "50px",
                        maxWidth: "300px",
                        width: "10vw",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={ExtractId(row.id) || undefined}
                    >
                      {ExtractId(row.id)}
                    </div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "200px" }}>{row.name}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "200px" }}>{row.address}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "100px" }}>{row.phone}</div>
                  </TableCell>
                  {!isViewMode && (
                    <>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{ color: "#FFF" }}
                          onClick={() => {
                            handleEdit(`${row.id}`)
                          }}
                        >
                          編集
                        </Button>
                      </TableCell>
                      <TableCell align="right">
                        {row.facility_count == 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ color: "#FFF" }}
                            onClick={() => {
                              handleDelete(`${row.id}`)
                            }}
                          >
                            削除
                          </Button>
                        )}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
      {/* ページャー */}
      <Paginator
        key={"ManageEstateTileListPaginator"}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </DashboardLayout>
  )
}

export default ManageEstateTileList
